// The dashboard's view of the Rust lane: /api/admin/rust/* (relay/src/routes/rustLane.ts). Types mirror the
// relay's summaries loosely on purpose: the engine's admin API is the source of truth and the pages read
// its bodies defensively, so a new engine field never breaks a render.

import { apiFetch } from '@/stores/authStore';

export type Json = Record<string, unknown>;

export interface RshLane { lane: string; state: string; claimed?: number; current?: number }
export interface RshBox {
  id: string;
  url: string;
  rank: number;
  ok: boolean;
  error: string | null;
  ms: number;
  raw: Json | null;
  summary: null | {
    id: string; ready: boolean; markets: number; books: number; consumers: number; sports: number;
    lanes: RshLane[]; lanesValid: boolean; startedAtMs: number | null; incarnation: string | null; directoryGeneration: number | null;
  };
}
export interface FeedgwUpstream { id: string; edgeId: string | null; tier: string | null; connected: boolean; rows: number; books: number; lastFrameAgeMs: number | null }
export interface SysHealth {
  at: number;
  serverTs: number;
  strategiesConfigured: boolean;
  rsh: RshBox[];
  feedgw: {
    ok: boolean; error: string | null; ms: number; raw: Json | null;
    summary: null | {
      publisher: string | null; floorError: string | null; floorEdges: Array<{ edgeId: string; live: boolean; sports: number }>;
      fallbackLabels: string[]; duplicateRowsDropped: number; tickers: number; clients: number; framesOutPerS: number | null; upstreams: FeedgwUpstream[];
    };
  };
  engine: {
    ok: boolean; error: string | null; ms: number; raw: Json | null;
    summary: null | { feed: Json | null; account: Json | null; kill: unknown; instances: Json[]; latency: Json | null; accounts: unknown; gateway: unknown };
  };
  relay: { hydraFleet: unknown; accountHub: unknown };
}

export interface Histo { n: number; p50Us: number; p95Us: number; p99Us: number; maxUs: number; meanUs: number; perSec: number }

export interface Instance {
  id: string;
  kind: string;
  user: string;
  userId?: string;
  mode: 'shadow' | 'live';
  armed: boolean;
  game?: string | null;
  target: Json;
  blocked?: string | null;
  error?: string | null;
  paused?: boolean;
  pulled?: boolean;
  live?: number;
  unitUsd?: number;
  exposureUsd?: number;
  books?: string[];
  desired?: Array<{ ticker: string; side: string; price: number; count_h: number; action: string; slot: number; postOnly?: boolean }>;
  strategy?: Json | null;
  [k: string]: unknown;
}

export interface Decision { id: string; at: string; atMs: string | number; instanceId: string; trigger: string; quotes: Array<{ ticker: string; side: string; price: number; count_h: number; action: string; slot: number }> }

export interface DeployResult { game: string; user: string; id: string | null; armed: boolean; status: number; error: string | null }

export interface Kind { name: string; about?: string; example?: Json; params?: Json; flow?: boolean }

export interface GameMarket { ticker: string; class: string; period?: string; status?: string; subtitle?: string; eventTicker?: string; yesBid?: number | null; yesAsk?: number | null; lastPrice?: number | null; volume?: number | null }
export interface Game { key: string; markets: GameMarket[]; [k: string]: unknown }

export class RustLaneError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'RustLaneError';
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; code?: string };
    throw new RustLaneError(res.status, b.code ?? `http_${res.status}`, b.error ?? `HTTP ${res.status}`);
  }
  return body as T;
}

const BASE = '/api/admin/rust';

export const rustLane = {
  syshealth: (signal?: AbortSignal) => apiFetch(`${BASE}/syshealth`, { signal }).then(parse<SysHealth>),
  status: (signal?: AbortSignal) => apiFetch(`${BASE}/strategies/status`, { signal }).then(parse<Json>),
  kinds: (signal?: AbortSignal) => apiFetch(`${BASE}/strategies/kinds`, { signal }).then(parse<Kind[] | { kinds: Kind[] }>).then((b) => (Array.isArray(b) ? b : b.kinds ?? [])),
  games: (sport: string, q: string, signal?: AbortSignal) =>
    apiFetch(`${BASE}/strategies/games?${new URLSearchParams(q ? { sport, q } : { sport })}`, { signal })
      .then(parse<Game[] | { games: Game[] }>).then((b) => (Array.isArray(b) ? b : b.games ?? [])),
  instances: (signal?: AbortSignal) =>
    apiFetch(`${BASE}/strategies/instances`, { signal }).then(parse<Instance[] | { instances: Instance[] }>).then((b) => (Array.isArray(b) ? b : b.instances ?? [])),
  instance: (id: string, signal?: AbortSignal) => apiFetch(`${BASE}/strategies/instances/${id}`, { signal }).then(parse<Instance>),
  decisions: (instance: string | null, limit: number, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (instance) qs.set('instance', instance);
    return apiFetch(`${BASE}/strategies/decisions?${qs}`, { signal }).then(parse<{ rows: Decision[] }>).then((b) => b.rows);
  },
  events: (instance: string | null, limit: number, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (instance) qs.set('instance', instance);
    return apiFetch(`${BASE}/strategies/events?${qs}`, { signal }).then(parse<{ rows: Json[] }>).then((b) => b.rows);
  },
  orders: (instance: string | null, limit: number, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (instance) qs.set('instance', instance);
    return apiFetch(`${BASE}/strategies/orders?${qs}`, { signal }).then(parse<{ rows: Json[] }>).then((b) => b.rows);
  },
  create: (body: { kind: string; target: Json; user: string; params: Json; unit_usd: number; mode: 'shadow' | 'live'; reason: string }) =>
    apiFetch(`${BASE}/strategies/instances`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then(parse<Instance>),
  action: (id: string, action: 'arm' | 'disarm' | 'delete', reason: string) =>
    apiFetch(`${BASE}/strategies/instances/${id}/${action}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ reason }) }).then(parse<Json>),
  mode: (id: string, mode: 'shadow' | 'live', reason: string) =>
    apiFetch(`${BASE}/strategies/instances/${id}/mode`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ mode, reason }) }).then(parse<Json>),
  params: (id: string, params: Json, reason: string) =>
    apiFetch(`${BASE}/strategies/instances/${id}/params`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ params, reason }) }).then(parse<Json>),
  deploy: (body: { kind: string; games: string[]; users: string[]; params: Json; unit_usd: number; mode: 'shadow' | 'live'; arm: boolean; reason: string }) =>
    apiFetch(`${BASE}/strategies/deploy`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
      .then(parse<{ results: DeployResult[]; created: number; armed: number }>),
  kill: (scope: 'strategies' | 'account', reason: string) =>
    apiFetch(`${BASE}/strategies/kill`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ scope, reason }) }).then(parse<Json>),
  killReset: (reason: string) =>
    apiFetch(`${BASE}/strategies/kill/reset`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ reason }) }).then(parse<Json>),
};

// ── small formatters shared by both pages ──
export const fmt = {
  int: (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—'),
  usd: (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(2)}` : '—'),
  cents: (cc: unknown) => (typeof cc === 'number' && Number.isFinite(cc) ? `${(cc / 100).toFixed(0)}¢` : '—'),
  us: (us: unknown) => {
    if (typeof us !== 'number' || !Number.isFinite(us)) return '—';
    if (us >= 1_000_000) return `${(us / 1_000_000).toFixed(2)} s`;
    if (us >= 1_000) return `${(us / 1_000).toFixed(1)} ms`;
    return `${us.toFixed(0)} µs`;
  },
  ms: (ms: unknown) => (typeof ms === 'number' && Number.isFinite(ms) ? (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms.toFixed(0)} ms`) : '—'),
  age: (ms: number) => (ms < 1000 ? `${ms} ms` : ms < 60_000 ? `${(ms / 1000).toFixed(0)} s` : ms < 3_600_000 ? `${(ms / 60_000).toFixed(0)} m` : `${(ms / 3_600_000).toFixed(1)} h`),
  gameLabel: (key: string | null | undefined) => {
    if (!key) return '—';
    const parts = key.split('|');
    if (parts.length >= 5) return `${parts[3]} v ${parts[4]}`;
    // An event ticker (esports): KXCS2GAME-26OCT230900LNALKAA → CS2 · 26OCT230900LNALKAA
    const m = /^KX([A-Z0-9]+?)GAME-(.+)$/.exec(key);
    return m ? `${m[1]} · ${m[2]}` : key;
  },
};

export function histo(v: unknown): Histo | null {
  if (!v || typeof v !== 'object') return null;
  const h = v as Partial<Histo>;
  return typeof h.p50Us === 'number' ? { n: h.n ?? 0, p50Us: h.p50Us, p95Us: h.p95Us ?? 0, p99Us: h.p99Us ?? 0, maxUs: h.maxUs ?? 0, meanUs: h.meanUs ?? 0, perSec: h.perSec ?? 0 } : null;
}
